import { formatMonthDay } from '@trips/shared/format';
import { isActivePerk, perkQuantity, perkStatus } from '@trips/shared/perks';
import { programInfo } from '@trips/shared/valuations';
import {
  centsPerPoint,
  dueSoon,
  pointsValue,
  pointValue,
  redemptionProgram,
} from '@trips/shared/wallet';
import { type ReactNode, useMemo, useState } from 'react';
import { selectPrefs, selectToday, selectWallet } from '../../data/selectors';
import { useStore } from '../../data/store';
import { Link, navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { CppCompare } from './CppCompare';
import { PerkSheet } from './PerkSheet';
import s from './Wallet.module.css';

export const usd = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
export const pts = (n: number) => n.toLocaleString('en-US');

/** Points grouped the way they're used: transferable card points first. */
const GROUPS = [
  { kind: 'bank', title: 'Credit card points' },
  { kind: 'airline', title: 'Airlines' },
  { kind: 'hotel', title: 'Hotels' },
  { kind: 'other', title: 'Other' },
] as const;

const FOLDED_KEY = 'trips.wallet.folded';

/** Which sections are closed, remembered on this device. */
function readFolded(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(FOLDED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function saveFolded(id: string, folded: boolean) {
  try {
    const set = readFolded();
    if (folded) set.add(id);
    else set.delete(id);
    localStorage.setItem(FOLDED_KEY, JSON.stringify([...set]));
  } catch {
    // Without storage, sections just open again next time.
  }
}

/** A section whose heading opens and closes it. */
function Fold({
  id,
  title,
  aside,
  children,
  closed,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  /** Starts folded every time (and isn't remembered). */
  closed?: boolean;
}) {
  const [open, setOpen] = useState(() => !closed && !readFolded().has(id));
  return (
    <section className={s.section}>
      <h2 className={s.foldHead}>
        <button
          type="button"
          className={s.foldButton}
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
            if (!closed) saveFolded(id, open);
          }}
        >
          <Icon name="caret-right" size={14} className={s.caret} />
          <span className={s.foldTitle}>{title}</span>
          {aside != null && <span className="num">{aside}</span>}
        </button>
      </h2>
      {open && children}
    </section>
  );
}

function when(days: number, on: string, prefs: ReturnType<typeof selectPrefs>) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 14) return `In ${days} days`;
  return formatMonthDay(on, prefs);
}

export function WalletScreen() {
  const wallet = useStore(selectWallet);
  const today = useStore(selectToday);
  const prefs = useStore(selectPrefs);
  const soon = useMemo(() => dueSoon(wallet, today), [wallet, today]);
  // Every certificate, upgrade and credit still to use, soonest to expire first; then the
  // used and expired ones, most recent first.
  const awards = useMemo(
    () =>
      wallet.perks
        .filter((p) => isActivePerk(p, today))
        .sort((a, b) => (a.expiresOn ?? '9999').localeCompare(b.expiresOn ?? '9999')),
    [wallet.perks, today],
  );
  const spent = useMemo(
    () =>
      wallet.perks
        .filter((p) => !p.deleted && !isActivePerk(p, today))
        .sort((a, b) =>
          (b.usedOn ?? b.expiresOn ?? '').localeCompare(a.usedOn ?? a.expiresOn ?? ''),
        ),
    [wallet.perks, today],
  );
  const [openPerk, setOpenPerk] = useState<string | null>(null);
  const cppOf = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof pointValue>>();
    return (programId: string) => {
      if (!cache.has(programId)) cache.set(programId, pointValue(programId, wallet.redemptions));
      return cache.get(programId) ?? null;
    };
  }, [wallet.redemptions]);
  const worth = (list: typeof wallet.programs, k: 'low' | 'main' | 'high') =>
    list.reduce((n, p) => n + pointsValue(p.balance, cppOf(p.programId)?.[k] ?? 0), 0);
  const total = worth(wallet.programs, 'main');
  const empty =
    !wallet.programs.length &&
    !wallet.cards.length &&
    !wallet.perks.length &&
    !wallet.redemptions.length;

  return (
    <div className={s.screen}>
      <header className={s.header}>
        <h1 className="t-title1">Wallet</h1>
        <button
          type="button"
          className={s.add}
          aria-label="Add to wallet"
          onClick={() => navigate('/wallet/new/choose', { overlay: true })}
        >
          <Icon name="plus" size={20} />
        </button>
      </header>

      {empty ? (
        <EmptyState
          icon="credit-card"
          title="Your points and cards"
          action={
            <Button
              variant="primary"
              icon="plus"
              onClick={() => navigate('/wallet/new/choose', { overlay: true })}
            >
              Add
            </Button>
          }
        >
          <p>Add point balances and cards, or bring everything over from Claude in Settings.</p>
        </EmptyState>
      ) : (
        <>
          {wallet.programs.length > 0 && (
            <div className={s.total}>
              <span className={s.totalLabel}>Points and miles</span>
              <span className={`${s.totalValue} num`}>
                {pts(wallet.programs.reduce((n, p) => n + p.balance, 0))}
              </span>
              <span className={`${s.totalLabel} num`}>
                Worth about {usd(total)} ({usd(worth(wallet.programs, 'low'))} to{' '}
                {usd(worth(wallet.programs, 'high'))})
              </span>
              <Link to="/wallet/history" className={s.overTime}>
                <Icon name="chart-line-up" size={16} />
                Over time
              </Link>
            </div>
          )}

          {soon.length > 0 && (
            <Fold id="soon" title="Soon">
              <div className={s.rows}>
                {soon.map((e) => (
                  <Link
                    key={`${e.kind}-${e.id}-${e.title}`}
                    to={
                      e.kind === 'perk'
                        ? `/wallet/edit/perk/${e.id}`
                        : e.kind === 'points'
                          ? `/wallet/points/${e.id}`
                          : `/wallet/cards/${e.id}`
                    }
                    className={s.row}
                  >
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>{e.title}</span>
                      <span className={s.rowDetail}>{e.detail}</span>
                    </span>
                    <span className={`${s.rowValue} ${e.days <= 7 ? s.urgent : ''} num`}>
                      {when(e.days, e.on, prefs)}
                    </span>
                  </Link>
                ))}
              </div>
            </Fold>
          )}

          {GROUPS.map((g) => {
            const list = wallet.programs.filter(
              (p) => (programInfo(p.programId)?.kind ?? 'other') === g.kind,
            );
            if (!list.length) return null;
            const groupPoints = list.reduce((n, p) => n + p.balance, 0);
            return (
              <Fold
                key={g.kind}
                id={g.kind}
                title={g.title}
                aside={groupPoints > 0 ? pts(groupPoints) : null}
              >
                <div className={s.rows}>
                  {list.map((p) => {
                    const v = cppOf(p.programId);
                    return (
                      <Link key={p.id} to={`/wallet/points/${p.id}`} className={s.row}>
                        <span className={s.rowText}>
                          <span className={s.rowTitle}>{p.name}</span>
                          {(p.tier || p.expiresOn) && (
                            <span className={`${s.rowDetail} num`}>
                              {[
                                p.tier,
                                p.expiresOn &&
                                  `Expire ${formatMonthDay(p.expiresOn, prefs)}${p.expiresOn.slice(0, 4) !== today.slice(0, 4) ? `, ${p.expiresOn.slice(0, 4)}` : ''}`,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                        </span>
                        <span className={s.rowRight}>
                          <span className={`${s.rowValue} num`}>{pts(p.balance)}</span>
                          {v && (
                            <span className={`${s.rowDetail} num`}>
                              {usd(pointsValue(p.balance, v.main))}
                            </span>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </Fold>
            );
          })}

          {awards.length > 0 && (
            <Fold id="awards" title="Awards" aside={String(awards.length)}>
              <div className={s.rows}>
                {awards.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setOpenPerk(p.id)}
                    className={s.row}
                  >
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>
                        {p.title}
                        {perkQuantity(p) > 1 && (
                          <span className={`${s.qty} num`}>×{perkQuantity(p)}</span>
                        )}
                      </span>
                      {p.value != null && (
                        <span className={`${s.rowDetail} num`}>
                          {p.unit === 'usd' ? usd(p.value) : `${pts(p.value)} points`}
                        </span>
                      )}
                    </span>
                    <span className={`${s.rowValue} num`}>
                      {p.expiresOn
                        ? `${formatMonthDay(p.expiresOn, prefs)}${p.expiresOn.slice(0, 4) !== today.slice(0, 4) ? `, ${p.expiresOn.slice(0, 4)}` : ''}`
                        : 'No expiry'}
                    </span>
                  </button>
                ))}
              </div>
            </Fold>
          )}

          {spent.length > 0 && (
            <Fold id="spent" title="Used & expired" aside={String(spent.length)} closed>
              <div className={s.rows}>
                {spent.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setOpenPerk(p.id)}
                    className={s.row}
                  >
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>{p.title}</span>
                      <span className={s.rowDetail}>
                        {perkStatus(p, today) === 'used'
                          ? `Used${p.usedOn ? ` ${formatMonthDay(p.usedOn, prefs)}` : ''}${p.usedFor ? `, ${p.usedFor}` : ''}`
                          : `Expired${p.expiresOn ? ` ${formatMonthDay(p.expiresOn, prefs)}` : ''}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </Fold>
          )}
          <PerkSheet
            key={openPerk ?? 'none'}
            perk={openPerk ? (wallet.perks.find((p) => p.id === openPerk) ?? null) : null}
            today={today}
            prefs={prefs}
            onClose={() => setOpenPerk(null)}
          />

          {wallet.cards.length > 0 && (
            <Fold id="cards" title="Cards" aside={String(wallet.cards.length)}>
              <div className={s.rows}>
                {wallet.cards.map((c) => (
                  <Link key={c.id} to={`/wallet/cards/${c.id}`} className={s.row}>
                    <span className={s.rowText}>
                      <span className={s.rowTitle}>{c.name}</span>
                      <span className={s.rowDetail}>
                        {c.issuer}
                        {c.last4 ? `, ending ${c.last4}` : ''}
                      </span>
                    </span>
                    <span className={s.rowRight}>
                      <span className={`${s.rowValue} num`}>
                        {c.annualFee ? `${usd(c.annualFee)} a year` : 'No fee'}
                      </span>
                      {c.feeDueOn && (
                        <span className={s.rowDetail}>
                          Renews {formatMonthDay(c.feeDueOn, prefs)}
                        </span>
                      )}
                    </span>
                  </Link>
                ))}
              </div>
            </Fold>
          )}

          {wallet.redemptions.length > 0 && (
            <Fold id="redemptions" title="Redemptions">
              <div className={s.rows}>
                {wallet.redemptions.slice(0, 8).map((r) => {
                  const cpp = centsPerPoint(r);
                  return (
                    <Link key={r.id} to={`/wallet/edit/redemption/${r.id}`} className={s.row}>
                      <span className={s.rowText}>
                        <span className={s.rowTitle}>{r.title}</span>
                        <span className={`${s.rowDetail} num`}>
                          {pts(r.points)} points, {formatMonthDay(r.on, prefs)}
                        </span>
                      </span>
                      {cpp != null && <CppCompare cpp={cpp} programId={redemptionProgram(r)} />}
                    </Link>
                  );
                })}
              </div>
            </Fold>
          )}
        </>
      )}
    </div>
  );
}
